/**
 * MilestoneAgeGroupField.js
 * -------------------------
 * Purpose:
 *   Custom form field component for capturing developmental milestone ages with
 *   optional "Not sure" and "Not yet doing" toggle pill buttons.
 *
 * Business Context:
 *   - Developmental history forms ask guardians to recall ages at which a child
 *     reached specific milestones (e.g., first walked, first words, toilet trained).
 *   - Many guardians won't remember exact ages, so each milestone has an optional
 *     "Not sure" checkbox that can be ticked instead of providing an age.
 *   - Some children may not yet have reached a milestone, so a "Not yet doing"
 *     checkbox is also available as a mutually exclusive alternative.
 *   - Ages are typically entered in months (for very young milestones like sitting up)
 *     or years (for later milestones like toilet training).
 *
 * Three Mutually Exclusive Options Per Milestone:
 *   1. Enter an age (e.g., 12 months) — the guardian remembers when it happened
 *   2. "Not sure" — the guardian doesn't remember the age
 *   3. "Not yet doing" — the child hasn't reached this milestone yet
 *
 * Data Shape (stored in answers[questionId]):
 *   {
 *     sit_up: { age: 6, unit: 'months', notSure: false, notYetDoing: false },
 *     walk: { age: null, unit: 'months', notSure: true, notYetDoing: false },
 *     toilet_day: { age: null, unit: 'months', notSure: false, notYetDoing: true },
 *     ...
 *   }
 *
 * Usage in Schema:
 *   {
 *     "questionId": "developmental_milestones",
 *     "type": "milestoneAgeGroup",
 *     "label": "Do you know what age they first started doing the following?",
 *     "milestones": [
 *       { "milestoneId": "sit_up", "label": "Sit up without support", "refId": "3.2.2.1.1" },
 *       { "milestoneId": "walk", "label": "Walk", "refId": "3.2.2.1.5" },
 *       ...
 *     ]
 *   }
 */

import React, { useMemo, useCallback } from 'react';

/**
 * MilestoneAgeGroupField
 * 
 * @param {Object} props
 * @param {Object} props.question - The question object from schema
 * @param {Object} props.value - Current answer value (keyed by milestoneId)
 * @param {Function} props.onChange - Callback when value changes: onChange(newValue)
 */
export default function MilestoneAgeGroupField({ question, value, onChange }) {
  // Normalize value to always be an object
  const currentValue = useMemo(() => {
    return typeof value === 'object' && value !== null ? value : {};
  }, [value]);

  // Extract milestones from the question definition
  // Wrap in useMemo to keep a stable reference for hook dependencies.
  // This avoids re-running downstream useMemos on every render when `question?.milestones`
  // is undefined (because `[]` would be a fresh array each time).
  const milestones = useMemo(() => {
    return Array.isArray(question?.milestones) ? question.milestones : [];
  }, [question?.milestones]);

  /**
   * Handle age input change for a specific milestone.
   * Clears both "notSure" and "notYetDoing" flags when a valid age is entered,
   * because the three options (age / not sure / not yet doing) are mutually exclusive.
   */
  const handleAgeChange = useCallback((milestoneId, ageStr) => {
    const age = ageStr === '' ? null : parseInt(ageStr, 10);
    const existing = currentValue[milestoneId] || {};
    const updated = {
      ...currentValue,
      [milestoneId]: {
        ...existing,
        age: Number.isFinite(age) ? age : null,
        // Auto-clear both "not sure" and "not yet doing" when user enters a valid age
        notSure: Number.isFinite(age) ? false : existing.notSure,
        notYetDoing: Number.isFinite(age) ? false : existing.notYetDoing,
      },
    };
    onChange(updated);
  }, [currentValue, onChange]);

  /**
   * Handle unit toggle (months vs years) for a specific milestone.
   */
  const handleUnitChange = useCallback((milestoneId, unit) => {
    const existing = currentValue[milestoneId] || {};
    const updated = {
      ...currentValue,
      [milestoneId]: {
        ...existing,
        unit,
      },
    };
    onChange(updated);
  }, [currentValue, onChange]);

  /**
   * Handle "Not sure" toggle (pill button).
   * Acts as a toggle: clicking when already active deselects it.
   * When activated, clears any entered age and deactivates "Not yet doing"
   * (the three options are mutually exclusive).
   */
  const handleNotSureToggle = useCallback((milestoneId) => {
    const existing = currentValue[milestoneId] || {};
    const wasActive = existing.notSure === true;
    const updated = {
      ...currentValue,
      [milestoneId]: {
        ...existing,
        notSure: !wasActive,
        // Clear age and "not yet doing" when activating "not sure"
        age: !wasActive ? null : existing.age,
        notYetDoing: !wasActive ? false : existing.notYetDoing,
      },
    };
    onChange(updated);
  }, [currentValue, onChange]);

  /**
   * Handle "Not yet doing" toggle (pill button).
   * Acts as a toggle: clicking when already active deselects it.
   * When activated, clears any entered age and deactivates "Not sure"
   * (the three options are mutually exclusive).
   *
   * Business context: Some children may not have reached a developmental milestone
   * yet (e.g., a young child who hasn't started talking). This is distinct from
   * "Not sure" where the guardian simply doesn't remember the age.
   */
  const handleNotYetDoingToggle = useCallback((milestoneId) => {
    const existing = currentValue[milestoneId] || {};
    const wasActive = existing.notYetDoing === true;
    const updated = {
      ...currentValue,
      [milestoneId]: {
        ...existing,
        notYetDoing: !wasActive,
        // Clear age and "not sure" when activating "not yet doing"
        age: !wasActive ? null : existing.age,
        notSure: !wasActive ? false : existing.notSure,
      },
    };
    onChange(updated);
  }, [currentValue, onChange]);

  /**
   * Determine default unit based on milestone type.
   * Earlier milestones (sitting, smiling, babbling, crawling) typically use months.
   * Later milestones (walking, talking, toilet training) may use either.
   */
  const getDefaultUnit = useCallback((milestoneId) => {
    // Milestones that typically happen in months
    const monthsMilestones = ['sit_up', 'smile', 'babble', 'crawl'];
    return monthsMilestones.includes(milestoneId) ? 'months' : 'months';
  }, []);

  return (
    <div className="space-y-4">
      {/* Reassurance about accuracy. Many guardians won't recall exact ages, so
          make plain that a rough estimate is acceptable and there's no need for
          precise dates. This is guidance about how to use the field (not the
          schema-authored question text, which the form renders under the prompt),
          so it lives with the widget it explains. */}
      <p className="text-sm text-gray-600">
        A rough or approximate age is fine — you do not need to remember the exact
        date. If you are not sure, use &ldquo;Not sure&rdquo;, or &ldquo;Not yet
        doing&rdquo; if they have not started yet.
      </p>

      {/* Milestone rows */}
      <div className="space-y-3">
        {milestones.map((milestone) => {
          const milestoneId = milestone.milestoneId;
          const entry = currentValue[milestoneId] || {};
          const age = entry.age;
          const unit = entry.unit || getDefaultUnit(milestoneId);
          const notSure = entry.notSure || false;
          const notYetDoing = entry.notYetDoing || false;
          // Age input and unit selector are disabled when either "Not sure" or "Not yet doing" is selected
          const ageDisabled = notSure || notYetDoing;

          return (
            <div
              key={milestoneId}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              {/* Milestone label */}
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={`${question.questionId}_${milestoneId}_age`}
                  className="block text-sm font-medium text-gray-900"
                >
                  {milestone.label}
                </label>
              </div>

              {/* Age input + unit selector — disabled when "Not sure" or "Not yet doing" is active */}
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id={`${question.questionId}_${milestoneId}_age`}
                  name={`${question.questionId}_${milestoneId}_age`}
                  min="0"
                  max={unit === 'months' ? 120 : 18}
                  value={ageDisabled ? '' : (age ?? '')}
                  onChange={(e) => handleAgeChange(milestoneId, e.target.value)}
                  disabled={ageDisabled}
                  placeholder={ageDisabled ? '-' : ''}
                  className={`
                    w-18 px-2 py-1.5 text-sm text-center border rounded-md
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                    ${ageDisabled 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                  `}
                  aria-describedby={`${question.questionId}_${milestoneId}_not_sure ${question.questionId}_${milestoneId}_not_yet_doing`}
                />

                {/* Unit selector */}
                <select
                  id={`${question.questionId}_${milestoneId}_unit`}
                  value={unit}
                  onChange={(e) => handleUnitChange(milestoneId, e.target.value)}
                  disabled={ageDisabled}
                  className={`
                    px-2 py-1.5 text-sm border rounded-md
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                    ${ageDisabled 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                  `}
                >
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
              </div>

              {/* "or" divider — separates the age/unit inputs from the alternative pill buttons */}
              <span className="text-xs text-gray-400 select-none">or</span>

              {/* Toggle pill buttons — styled to match RadioGroup / CheckboxGroup tile theming */}
              <div className="flex items-center gap-2">
                {/* "Not sure" pill button */}
                <button
                  type="button"
                  id={`${question.questionId}_${milestoneId}_not_sure`}
                  onClick={() => handleNotSureToggle(milestoneId)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                    border-2 cursor-pointer select-none whitespace-nowrap
                    transition-all duration-200 ease-out
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
                    ${notSure
                      ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }
                  `}
                  aria-pressed={notSure}
                >
                  {/* Selection indicator dot — mirrors the yellow check theme from RadioGroup */}
                  <span
                    className={`
                      flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 relative
                      transition-all duration-200 ease-out
                      ${notSure
                        ? 'bg-yellow-400 border-yellow-400'
                        : 'border-gray-300 bg-white'
                      }
                    `}
                    aria-hidden="true"
                  >
                    {notSure && (
                      <svg viewBox="0 0 16 16" className="absolute inset-0 w-2.5 h-2.5 m-auto fill-white">
                        <path d="M6.173 12.414a1 1 0 0 1-1.414 0L1.88 9.535a1 1 0 1 1 1.415-1.415l2.18 2.18 7.23-7.23a1 1 0 1 1 1.415 1.414l-8.947 8.93z"/>
                      </svg>
                    )}
                  </span>
                  Not sure
                </button>

                {/* "Not yet doing" pill button */}
                <button
                  type="button"
                  id={`${question.questionId}_${milestoneId}_not_yet_doing`}
                  onClick={() => handleNotYetDoingToggle(milestoneId)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                    border-2 cursor-pointer select-none whitespace-nowrap
                    transition-all duration-200 ease-out
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
                    ${notYetDoing
                      ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }
                  `}
                  aria-pressed={notYetDoing}
                >
                  {/* Selection indicator dot — mirrors the yellow check theme from RadioGroup */}
                  <span
                    className={`
                      flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 relative
                      transition-all duration-200 ease-out
                      ${notYetDoing
                        ? 'bg-yellow-400 border-yellow-400'
                        : 'border-gray-300 bg-white'
                      }
                    `}
                    aria-hidden="true"
                  >
                    {notYetDoing && (
                      <svg viewBox="0 0 16 16" className="absolute inset-0 w-2.5 h-2.5 m-auto fill-white">
                        <path d="M6.173 12.414a1 1 0 0 1-1.414 0L1.88 9.535a1 1 0 1 1 1.415-1.415l2.18 2.18 7.23-7.23a1 1 0 1 1 1.415 1.414l-8.947 8.93z"/>
                      </svg>
                    )}
                  </span>
                  Not yet doing
                </button>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
